// The "Regression Log" presentation: the CMS's published posts rendered as a
// passing test run. Suites are categories, cases are posts, flags are tags.
//
// Two reporters share one data set:
//   verbose — the terminal view, with expandable prose output per case
//   plain   — a conventional résumé layout, for readers the metaphor loses
//
// Filter state lives in the URL query string, so any filtered view is
// linkable (?reporter=plain&suite=project&grep=billing).

import { getMilestoneDetail } from './api';
import { SUITE_ORDER, suiteMetaFor } from './config';
import { escapeHtml } from './dom';
import { mountResume, renderArtifactsLine, resumeConfigFrom } from './resume';
import type { Milestone } from './types';

export type Reporter = 'verbose' | 'plain';

export interface RegressionState {
  reporter: Reporter;
  suite: string | null;
  grep: string | null;
  open: Set<string>;
}

/**
 * When a milestone actually happened, which is not when it was published.
 *
 * Falls back to `publishedAt` for posts that predate the `occurredAt` field —
 * and for the window between deploying this and deploying the CMS, where the
 * API response omits the key entirely.
 */
export function occurredAt(milestone: Milestone): string | null {
  return milestone.occurredAt ?? milestone.publishedAt;
}

/**
 * The year column. A milestone with an end date reads as a span; one without
 * reads as ongoing, which is what "2021 —" means on a résumé.
 */
function yearOf(milestone: Milestone): string {
  const start = occurredAt(milestone);
  if (!start) return '????';
  const from = start.slice(0, 4);
  if (!milestone.occurredAt) return from;
  const to = milestone.occurredUntil?.slice(0, 4);
  if (to === undefined) return `${from} —`;
  return to === from ? from : `${from}–${to}`;
}

function suiteOf(milestone: Milestone): string {
  return milestone.categories[0]?.slug ?? 'uncategorised';
}

const MS_PER_YEAR = 31_557_600_000;

/** Span from the earliest milestone to now, as a test-runner duration. */
export function elapsed(milestones: Milestone[]): string {
  const times = milestones
    .map(occurredAt)
    .filter((iso): iso is string => iso !== null)
    .map((iso) => Date.parse(iso))
    .filter((t) => !Number.isNaN(t));
  if (times.length === 0) return '0s';
  const span = Date.now() - Math.min(...times);
  if (span < MS_PER_YEAR) return `${(span / 86_400_000).toFixed(0)}d`;
  return `${(span / MS_PER_YEAR).toFixed(1)}y`;
}

export function suitesOf(milestones: Milestone[]): string[] {
  const seen = [...new Set(milestones.map(suiteOf))];
  return seen.sort((a, b) => {
    const ai = SUITE_ORDER.indexOf(a);
    const bi = SUITE_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
}

export function applyFilters(milestones: Milestone[], state: RegressionState): Milestone[] {
  return milestones.filter((m) => {
    if (state.suite && suiteOf(m) !== state.suite) return false;
    if (state.grep && !m.tags.some((t) => t.slug === state.grep)) return false;
    return true;
  });
}

/* ---------- URL <-> state ---------- */

export function stateFromUrl(): RegressionState {
  const params = new URLSearchParams(window.location.search);
  const reporter = params.get('reporter') === 'plain' ? 'plain' : 'verbose';
  return {
    reporter,
    suite: params.get('suite'),
    grep: params.get('grep'),
    open: new Set<string>(),
  };
}

function syncUrl(state: RegressionState): void {
  const params = new URLSearchParams();
  if (state.reporter !== 'verbose') params.set('reporter', state.reporter);
  if (state.suite) params.set('suite', state.suite);
  if (state.grep) params.set('grep', state.grep);
  const query = params.toString();
  const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(null, '', url);
}

/* ---------- rendering ---------- */

function renderCommand(state: RegressionState): string {
  const flags = [`--reporter=${state.reporter}`];
  if (state.suite) flags.push(`--suite=${state.suite}`);
  if (state.grep) flags.push(`--grep=${state.grep}`);
  return `
    <span class="rl-prompt">$</span>
    <span class="rl-cmd">npx career</span>
    ${flags.map((f) => `<span class="rl-flag-text">${escapeHtml(f)}</span>`).join(' ')}
  `;
}

function renderStats(all: Milestone[], shown: Milestone[]): string {
  const filtered = shown.length !== all.length;
  const count = filtered
    ? `${shown.length} <span class="rl-of">of ${all.length}</span>`
    : `${shown.length}`;
  return `
    <span>SUITES <i>${suitesOf(shown).length}</i></span>
    <span>CASES <i>${count}</i></span>
    <span>PASSED <i class="rl-ok">${shown.length}</i></span>
    <span>FAILED <i class="rl-zero">0</i></span>
    <span>ELAPSED <i>${elapsed(all)}</i></span>
  `;
}

function renderSuiteFlags(all: Milestone[], state: RegressionState): string {
  const buttons = [
    `<button class="rl-flag" type="button" data-suite="" aria-pressed="${state.suite === null && state.grep === null}">all</button>`,
    ...suitesOf(all).map(
      (s) =>
        `<button class="rl-flag" type="button" data-suite="${escapeHtml(s)}" aria-pressed="${state.suite === s}">${escapeHtml(s)}</button>`,
    ),
  ];
  if (state.grep) {
    buttons.push(
      `<button class="rl-flag" type="button" data-clear-grep="1" aria-pressed="true">grep:${escapeHtml(state.grep)} ✕</button>`,
    );
  }
  return buttons.join('');
}

function renderTags(milestone: Milestone): string {
  if (milestone.tags.length === 0) {
    return `<span class="rl-notags">no tags on this post</span>`;
  }
  return milestone.tags
    .map(
      (t) =>
        `<button class="rl-tag" type="button" data-grep="${escapeHtml(t.slug)}">${escapeHtml(t.name)}</button>`,
    )
    .join('');
}

/** The stdout header line — carries location and dates when the CMS has them. */
function detailMeta(milestone: Milestone): string {
  const bits = [milestone.location, milestone.organization].filter(
    (v): v is string => typeof v === 'string' && v.length > 0,
  );
  return bits.length > 0 ? `stdout · ${escapeHtml(bits.join(' · '))}` : 'stdout';
}

function renderCase(milestone: Milestone, state: RegressionState, index: number): string {
  const isOpen = state.open.has(milestone.id);
  const assertion = milestone.excerpt
    ? `<em>— ${escapeHtml(milestone.excerpt)}</em>`
    : `<em class="rl-noexcerpt">— no excerpt set</em>`;
  const org = milestone.organization
    ? `<span class="rl-org">${escapeHtml(milestone.organization)}</span> `
    : '';

  return `
    <div class="rl-case">
      <button
        class="rl-row"
        type="button"
        data-case="${escapeHtml(milestone.id)}"
        aria-expanded="${isOpen}"
        style="animation-delay:${index * 55}ms"
      >
        <span class="rl-verdict">✓ PASS</span>
        <span class="rl-name">${org}${escapeHtml(milestone.title)} ${assertion}</span>
        <span class="rl-year">${yearOf(milestone)}</span>
      </button>
      ${
        isOpen
          ? `<div class="rl-detail" data-detail-for="${escapeHtml(milestone.id)}">
               <div class="rl-stdout">${detailMeta(milestone)}</div>
               <div class="rl-prose" data-prose>Loading…</div>
               <div class="rl-taglist">${renderTags(milestone)}</div>
             </div>`
          : ''
      }
    </div>
  `;
}

function renderVerbose(shown: Milestone[], state: RegressionState): string {
  let index = 0;
  const groups = suitesOf(shown).map((suite) => {
    const inSuite = shown.filter((m) => suiteOf(m) === suite);
    const meta = suiteMetaFor(suite);
    const cases = inSuite.map((m) => renderCase(m, state, index++)).join('');
    return `<div class="rl-suitehead">${escapeHtml(meta.label)} <b>· ${inSuite.length}</b></div>${cases}`;
  });
  return `<div class="rl-rows rl-anim">${groups.join('')}</div>`;
}

function renderPlain(shown: Milestone[]): string {
  const sections = suitesOf(shown).map((suite) => {
    const meta = suiteMetaFor(suite);
    const entries = shown
      .filter((m) => suiteOf(m) === suite)
      .map(
        (m) => `
        <article class="rl-entry">
          <div class="rl-entry-top">
            <h3 class="rl-entry-title">${escapeHtml(m.title)}</h3>
            <span class="rl-entry-date">${yearOf(m)}</span>
          </div>
          ${
            m.organization
              ? `<div class="rl-entry-org">${escapeHtml(m.organization)}${
                  m.location ? ` · ${escapeHtml(m.location)}` : ''
                }</div>`
              : ''
          }
          ${m.excerpt ? `<p class="rl-entry-body">${escapeHtml(m.excerpt)}</p>` : ''}
          ${
            m.tags.length > 0
              ? `<div class="rl-entry-tags">${m.tags.map((t) => escapeHtml(t.name)).join(' · ')}</div>`
              : ''
          }
        </article>`,
      )
      .join('');
    return `<section class="rl-plain-sec"><h2>${escapeHtml(meta.label)}</h2>${entries}</section>`;
  });
  return `<div class="rl-plain">${sections.join('')}</div>`;
}

function renderEmpty(state: RegressionState): string {
  const why = state.suite || state.grep ? 'matched that filter' : 'published yet';
  return `
    <div class="rl-rows">
      <div class="rl-empty">
        <div>0 cases ${why}.</div>
        ${
          state.suite || state.grep
            ? `<button class="rl-flag" type="button" data-suite="">reset filters</button>`
            : `<div class="rl-empty-hint">Publish a post in the CMS and it appears here.</div>`
        }
      </div>
    </div>
  `;
}

/**
 * Skills come from the `skills.matrix` setting as one group per line:
 *
 *   Test Automation: Appium, Selenium WebDriver, TestNG
 *   Languages: Java, Bash, SQL
 *
 * Chosen over JSON because it's edited in a plain textarea in the admin — a
 * missing brace shouldn't be able to blank the section. Malformed lines are
 * skipped rather than thrown on.
 */
export function parseSkills(raw: string | undefined): { group: string; items: string[] }[] {
  if (!raw) return [];
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && line.includes(':'))
    .map((line) => {
      const separator = line.indexOf(':');
      const group = line.slice(0, separator).trim();
      const items = line
        .slice(separator + 1)
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
      return { group, items };
    })
    .filter((entry) => entry.group.length > 0 && entry.items.length > 0);
}

function renderSkills(raw: string | undefined): string {
  const groups = parseSkills(raw);
  if (groups.length === 0) return '';
  const rows = groups
    .map(
      (g) => `
        <div class="rl-skill-row">
          <div class="rl-skill-group">${escapeHtml(g.group)}</div>
          <div class="rl-skill-items">
            ${g.items.map((i) => `<span class="rl-skill">${escapeHtml(i)}</span>`).join('')}
          </div>
        </div>`,
    )
    .join('');
  return `
    <section class="rl-skills">
      <div class="rl-suitehead rl-skills-head">environment</div>
      ${rows}
    </section>
  `;
}

export function renderOutput(
  all: Milestone[],
  state: RegressionState,
  settings: Record<string, string> = {},
): string {
  const resume = resumeConfigFrom(settings);
  const shown = applyFilters(all, state);
  const body =
    shown.length === 0
      ? renderEmpty(state)
      : state.reporter === 'verbose'
        ? renderVerbose(shown, state)
        : renderPlain(shown);

  return `
    <div class="rl-cmdline">${renderCommand(state)}</div>
    <div class="rl-stats">${renderStats(all, shown)}</div>
    ${renderArtifactsLine(resume)}
    <div class="rl-controls">
      <div class="rl-group">
        <span class="rl-ctl-label">suite</span>
        ${renderSuiteFlags(all, state)}
      </div>
      <div class="rl-group">
        <span class="rl-ctl-label">reporter</span>
        <button class="rl-flag rl-rep" type="button" data-reporter="verbose" aria-pressed="${state.reporter === 'verbose'}">verbose</button>
        <button class="rl-flag rl-rep" type="button" data-reporter="plain" aria-pressed="${state.reporter === 'plain'}">plain</button>
      </div>
    </div>
    <div class="rl-output">${body}</div>
    ${renderSkills(settings['skills.matrix'])}
    <div class="rl-summary">
      <span class="rl-ok">✓ ${shown.length} passed</span>, 0 failed. Done in ${elapsed(all)}.
    </div>
  `;
}

/* ---------- interactions ---------- */

const detailCache = new Map<string, string>();

async function loadDetail(id: string): Promise<string> {
  const cached = detailCache.get(id);
  if (cached !== undefined) return cached;
  const detail = await getMilestoneDetail(id);
  detailCache.set(id, detail.contentHtml);
  return detail.contentHtml;
}

/** Fills any open-but-unloaded detail panel with its post body. */
async function hydrateOpenDetails(root: HTMLElement): Promise<void> {
  const panels = root.querySelectorAll<HTMLElement>('[data-detail-for]');
  await Promise.all(
    [...panels].map(async (panel) => {
      const id = panel.dataset['detailFor'];
      const prose = panel.querySelector<HTMLElement>('[data-prose]');
      if (!id || !prose) return;
      try {
        prose.innerHTML = await loadDetail(id);
      } catch {
        prose.innerHTML = `<p class="rl-loaderr">Couldn’t load this entry's output.</p>`;
      }
    }),
  );
}

export function mountRegressionLog(
  root: HTMLElement,
  all: Milestone[],
  settings: Record<string, string> = {},
): void {
  const state = stateFromUrl();

  const resume = resumeConfigFrom(settings);
  // Mounted once: the pill lives on <body> and must survive repaints. Only the
  // observer is re-attached below, since innerHTML replaces the line it watches.
  const controller = resume ? mountResume(resume, all) : null;

  const paint = (): void => {
    root.innerHTML = renderOutput(all, state, settings);
    void hydrateOpenDetails(root);
    controller?.observe(root);
    syncUrl(state);
  };

  root.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-reporter],[data-suite],[data-case],[data-grep],[data-clear-grep]',
    );
    if (!target) return;

    const { reporter, suite, grep, clearGrep, case: caseId } = target.dataset;

    if (reporter) {
      state.reporter = reporter === 'plain' ? 'plain' : 'verbose';
    } else if (clearGrep) {
      state.grep = null;
    } else if (grep) {
      state.grep = grep;
      state.suite = null;
    } else if (suite !== undefined) {
      state.suite = suite === '' ? null : suite;
      if (suite === '') state.grep = null;
    } else if (caseId) {
      if (state.open.has(caseId)) state.open.delete(caseId);
      else state.open.add(caseId);
    } else {
      return;
    }

    paint();
  });

  paint();
}
